import React from 'react';
import { useLocation } from 'react-router-dom';
import MarketingProvider from './MarketingProvider';
import MarketingDashboardPreview from './MarketingDashboardPreview';
import MarketingLessonPreview from './MarketingLessonPreview';
import MarketingProductionTracker from './MarketingProductionTracker';
import MarketingProductionGoals from './MarketingProductionGoals';
import MarketingMobileFrame from './MarketingMobileFrame';
import MarketingStep1Preview from './MarketingStep1Preview';
import MarketingStep2Preview from './MarketingStep2Preview';
import MarketingStep3Preview from './MarketingStep3Preview';
import MarketingMyPathCompositePreview from './MarketingMyPathCompositePreview';
import MarketingAdditionalProgramsCoversPreview from './MarketingAdditionalProgramsCoversPreview';
import MarketingWhatsInsidePreview from './MarketingWhatsInsidePreview';
import MarketingEmailCompositePreview from './MarketingEmailCompositePreview';
import MarketingErrorBoundary from './MarketingErrorBoundary';
import {
    MARKETING_EXPORT_NOTE,
    MARKETING_FRAMEWORK_OVERVIEW,
    MARKETING_HOW_TO_USE,
    MARKETING_PAGE,
    MARKETING_PHASES,
    MARKETING_SECTION_COPY,
    MARKETING_SECTION_NAV,
    MARKETING_SECTION_SIZES,
} from './marketingSectionCopy';
import './MarketingAssets.css';

function SectionGuide({ sectionCopy, captureMode }) {
    if (captureMode) {
        return null;
    }

    return (
        <div className="marketing-section__guide">
            {sectionCopy.phase ? (
                <span className="marketing-section__phase">{sectionCopy.phase}</span>
            ) : null}
            <h2 className="marketing-section__title">{sectionCopy.title}</h2>
            {sectionCopy.angle ? (
                <p className="marketing-section__angle">
                    &ldquo;{sectionCopy.angle}&rdquo;
                </p>
            ) : null}
            {sectionCopy.summary ? (
                <p className="marketing-section__summary">{sectionCopy.summary}</p>
            ) : null}
            {sectionCopy.includes?.length ? (
                <div className="marketing-section__includes-wrap">
                    <p className="marketing-section__includes-label">
                        What&apos;s included
                    </p>
                    <ul className="marketing-section__includes">
                        {sectionCopy.includes.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {sectionCopy.disclaimer ? (
                <div className="marketing-section__disclaimer">
                    {sectionCopy.disclaimer}
                </div>
            ) : null}
            {sectionCopy.programs?.length ? (
                <div className="marketing-section__programs-wrap">
                    <p className="marketing-section__includes-label">
                        Programs historically offered apart from the Framework
                    </p>
                    <ul className="marketing-section__programs">
                        {sectionCopy.programs.map((program) => (
                            <li key={program.title}>
                                <strong>{program.title}</strong>
                                {' — '}
                                {program.description}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {sectionCopy.shopCopy ? (
                <blockquote className="marketing-section__shop-copy">
                    <span className="marketing-section__shop-label">
                        Suggested copy
                    </span>
                    {sectionCopy.shopCopy}
                </blockquote>
            ) : null}
            {sectionCopy.recommendedFor ? (
                <p className="marketing-section__recommended">
                    <strong>Recommended for:</strong>{' '}
                    {sectionCopy.recommendedFor}
                </p>
            ) : null}
        </div>
    );
}

function ProductPreview({
    id,
    sectionCopy,
    children,
    className = '',
    captureMode,
}) {
    return (
        <section id={id} className={`marketing-section ${className}`.trim()}>
            <SectionGuide sectionCopy={sectionCopy} captureMode={captureMode} />
            <div className="marketing-section__preview-wrap">
                {!captureMode ? (
                    <p className="marketing-section__preview-label">
                        Member interface preview
                    </p>
                ) : null}
                <div
                    className={`marketing-capture marketing-capture--${className}`}
                >
                    <MarketingErrorBoundary>{children}</MarketingErrorBoundary>
                </div>
            </div>
        </section>
    );
}

/**
 * Marketing-facing product guide — V1 Framework only.
 * Route: /marketing-assets
 * Add ?capture=1 to hide guide text (internal asset export).
 */
const MarketingAssetsPage = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const captureMode = searchParams.get('capture');
    const frame = searchParams.get('frame');
    const sectionOnly = searchParams.get('section');
    const c = MARKETING_SECTION_COPY;

    const showSection = (id) => !sectionOnly || sectionOnly === id;

    if (frame === 'mobile') {
        return (
            <MarketingProvider>
                <div className="marketing-mobile-frame-root">
                    <MarketingDashboardPreview
                        variant="mobile"
                        showElearning
                        showOolaScore
                    />
                </div>
            </MarketingProvider>
        );
    }

    return (
        <MarketingProvider>
            <div
                className={`marketing-assets-page${
                    captureMode ? ' marketing-assets-page--capture' : ''
                }`}
            >
                {!captureMode ? (
                    <>
                        <header className="marketing-hero">
                            <p className="marketing-hero__eyebrow">
                                {MARKETING_PAGE.eyebrow}
                            </p>
                            <h1 className="marketing-hero__title">
                                {MARKETING_PAGE.title}
                            </h1>
                            <p className="marketing-hero__subtitle">
                                {MARKETING_PAGE.subtitle}
                            </p>
                            <div className="marketing-hero__notes">
                                <p>{MARKETING_PAGE.demoNote}</p>
                                <p>{MARKETING_PAGE.interfaceNote}</p>
                            </div>
                        </header>

                        <section className="marketing-overview">
                            <h2 className="marketing-overview__title">
                                {MARKETING_FRAMEWORK_OVERVIEW.title}
                            </h2>
                            <p className="marketing-overview__summary">
                                {MARKETING_FRAMEWORK_OVERVIEW.summary}
                            </p>
                            <ul className="marketing-overview__list">
                                {MARKETING_FRAMEWORK_OVERVIEW.bullets.map(
                                    (item) => (
                                        <li key={item}>{item}</li>
                                    )
                                )}
                            </ul>
                        </section>

                        <section className="marketing-phases">
                            <h2 className="marketing-phases__title">
                                The member journey
                            </h2>
                            <div className="marketing-phases__grid">
                                {MARKETING_PHASES.map((phase) => (
                                    <article
                                        key={phase.id}
                                        className="marketing-phase-card"
                                    >
                                        <span className="marketing-phase-card__label">
                                            {phase.label}
                                        </span>
                                        <h3 className="marketing-phase-card__name">
                                            {phase.name}
                                        </h3>
                                        <span className="marketing-phase-card__timing">
                                            {phase.timing}
                                        </span>
                                        <p className="marketing-phase-card__summary">
                                            {phase.summary}
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </section>

                        <section className="marketing-how-to">
                            <h2 className="marketing-how-to__title">
                                {MARKETING_HOW_TO_USE.title}
                            </h2>
                            <ol className="marketing-how-to__list">
                                {MARKETING_HOW_TO_USE.items.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ol>
                        </section>

                        <nav className="marketing-nav" aria-label="Sections">
                            <h2 className="marketing-nav__title">
                                Browse by section
                            </h2>
                            <div className="marketing-nav__links">
                                {MARKETING_SECTION_NAV.map((item, index) => (
                                    <a
                                        key={item.id}
                                        href={`/marketing-assets#${item.id}`}
                                        className="marketing-nav__link"
                                    >
                                        <span className="marketing-nav__num">
                                            {index + 1}
                                        </span>
                                        {item.label}
                                    </a>
                                ))}
                            </div>
                        </nav>
                    </>
                ) : null}

                {showSection('step1') ? (
                    <ProductPreview
                        id="step1"
                        sectionCopy={c.step1}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <MarketingStep1Preview />
                    </ProductPreview>
                ) : null}

                {showSection('step2') ? (
                    <ProductPreview
                        id="step2"
                        sectionCopy={c.step2}
                        className="step2"
                        captureMode={captureMode}
                    >
                        <MarketingStep2Preview />
                    </ProductPreview>
                ) : null}

                {showSection('step3') ? (
                    <ProductPreview
                        id="step3"
                        sectionCopy={c.step3}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <MarketingStep3Preview />
                    </ProductPreview>
                ) : null}

                {showSection('dashboard') ? (
                    <ProductPreview
                        id="dashboard"
                        sectionCopy={c.dashboard}
                        className="shop"
                        captureMode={captureMode}
                    >
                        <MarketingDashboardPreview />
                    </ProductPreview>
                ) : null}

                {showSection('whats-inside') ? (
                    <ProductPreview
                        id="whats-inside"
                        sectionCopy={c.whatsInside}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <MarketingWhatsInsidePreview />
                    </ProductPreview>
                ) : null}

                {showSection('elearning') ? (
                    <ProductPreview
                        id="elearning"
                        sectionCopy={c.elearning}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <MarketingLessonPreview />
                    </ProductPreview>
                ) : null}

                {showSection('tracker-ring') ? (
                    <ProductPreview
                        id="tracker-ring"
                        sectionCopy={c.trackerRing}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <div className="marketing-split marketing-split--production">
                            <MarketingProductionTracker initialTab="tracker" />
                            <MarketingProductionGoals />
                        </div>
                    </ProductPreview>
                ) : null}

                {showSection('tracker-goals') ? (
                    <ProductPreview
                        id="tracker-goals"
                        sectionCopy={c.trackerGoals}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <div className="marketing-split marketing-split--production">
                            <MarketingProductionTracker initialTab="daily" />
                            <MarketingProductionGoals />
                        </div>
                    </ProductPreview>
                ) : null}

                {showSection('desktop-mobile') ? (
                    <ProductPreview
                        id="desktop-mobile"
                        sectionCopy={c.desktopMobile}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <div className="marketing-device-composite">
                            <div className="marketing-device-composite__desktop">
                                <MarketingDashboardPreview />
                            </div>
                            <div className="marketing-device-composite__phone">
                                <div className="marketing-phone-frame">
                                    <MarketingMobileFrame />
                                </div>
                            </div>
                        </div>
                    </ProductPreview>
                ) : null}

                {showSection('mobile-only') ? (
                    <ProductPreview
                        id="mobile-only"
                        sectionCopy={c.mobileOnly}
                        className="mobile"
                        captureMode={captureMode}
                    >
                        <div className="marketing-phone-frame marketing-phone-frame--solo">
                            <MarketingMobileFrame />
                        </div>
                    </ProductPreview>
                ) : null}

                {showSection('email') ? (
                    <ProductPreview
                        id="email"
                        sectionCopy={c.email}
                        className="shop"
                        captureMode={captureMode}
                    >
                        <MarketingEmailCompositePreview />
                    </ProductPreview>
                ) : null}

                {showSection('my-path') ? (
                    <ProductPreview
                        id="my-path"
                        sectionCopy={c.myPath}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <MarketingMyPathCompositePreview />
                    </ProductPreview>
                ) : null}

                {showSection('additional-programs') ? (
                    <ProductPreview
                        id="additional-programs"
                        sectionCopy={c.additionalPrograms}
                        className="landscape"
                        captureMode={captureMode}
                    >
                        <MarketingAdditionalProgramsCoversPreview />
                    </ProductPreview>
                ) : null}

                {!captureMode ? (
                    <footer className="marketing-footer">
                        <p>
                            Questions about the Framework or need additional
                            assets? Contact the Oola product team.
                        </p>
                        <details className="marketing-footer__export">
                            <summary>{MARKETING_EXPORT_NOTE.title}</summary>
                            <p>{MARKETING_EXPORT_NOTE.body}</p>
                            <ul>
                                {Object.entries(MARKETING_SECTION_SIZES).map(
                                    ([id, size]) => (
                                        <li key={id}>
                                            <a href={`?capture=1&section=${id}`}>
                                                {id}
                                            </a>
                                            {' — '}
                                            {size}
                                        </li>
                                    )
                                )}
                            </ul>
                        </details>
                    </footer>
                ) : null}
            </div>
        </MarketingProvider>
    );
};

export default MarketingAssetsPage;
